"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  LABEL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  FORMULA_ERROR_STRINGS,
  type FormulaErrorStrings,
  formulaErrorMessage,
  WORKING_BOX,
} from "@/components/tools/chemistry/formula-view";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type Assignment,
  assignOxidation,
  type Environment,
  type FlagKey,
  stateText,
  stockNotation,
} from "@/lib/tool-kit/chem-reactions/oxidation";
import { frac } from "@/lib/tool-kit/chem-reactions/rational";
import { getElement } from "@/lib/tool-kit/chemistry/elements";

/** English strings. A translated content file overrides these through `ui`. */
export const OXIDATION_NUMBER_CALCULATOR_STRINGS = {
  formulaLabel: "Formula or ion",
  formulaHint:
    "Case matters: Co is cobalt, CO is carbon monoxide. Write a charge as SO4^2-, Fe^3+ or MnO4-. Brackets and hydrates work: K4[Fe(CN)6], CuSO4·5H2O.",
  formulaPlaceholder: "K2Cr2O7",
  tryLabel: "Try an example",
  results: "Results",
  enter: "Type a chemical formula or ion to assign oxidation numbers.",
  columnElement: "Element",
  columnAtoms: "Atoms",
  columnNumber: "Oxidation number",
  columnStock: "Stock notation",
  columnRule: "Rule used",
  perSpecies: "{species} (× {count})",
  speciesOnce: "{species}",
  sumHeading: "Check: the numbers add up to the charge",
  sumLine: "{terms} = {total}",
  sumTerm: "{count} × ({state})",
  noStock: "—",
  rules: {
    free: "An atom in an element is 0.",
    monatomic: "A monatomic ion's number equals its charge.",
    fluorine: "Fluorine is −1 in all compounds.",
    group1: "Group 1 metals are +1.",
    group2: "Group 2 metals are +2.",
    hydrogen: "Hydrogen is +1 with nonmetals.",
    hydride: "Hydrogen is −1 when bonded only to metals or boron (a hydride).",
    oxygen: "Oxygen is −2 in most compounds.",
    peroxide: "Oxygen is −1 in a peroxide, found from the sum.",
    superoxide: "Oxygen is −½ in a superoxide, found from the sum.",
    ozonide: "Oxygen is −⅓ in an ozonide, found from the sum.",
    oxygenFluoride:
      "Fluorine outranks oxygen, so oxygen takes what is left, which is positive.",
    halogen:
      "Cl, Br and I are −1 unless combined with oxygen, fluorine or a stronger halogen.",
    sum: "Found from the sum: the numbers add up to the charge of the species.",
    electronegativity:
      "The more electronegative element takes its usual negative number.",
    ion: "Fixed by the charge of the polyatomic ion it is paired with.",
  },
  flagsHeading: "Worth knowing",
  flags: {
    peroxide:
      "Peroxide: oxygen is −1 here, not −2, because the formula only balances with an O–O bond. Hydrogen peroxide and sodium peroxide are the usual examples.",
    superoxide:
      "Superoxide: oxygen is −½. A fractional number is an average over the O₂⁻ unit.",
    ozonide: "Ozonide: oxygen is −⅓, an average over the O₃⁻ unit.",
    hydride:
      "Hydride: hydrogen is −1 because it is bonded only to metals or boron, which are less electronegative. Most compounds of hydrogen have +1.",
    oxygenFluoride:
      "Oxygen is positive here because fluorine is the most electronegative element and always takes −1.",
    average:
      "Average: the number is a fraction because the element is in more than one environment (mixed valence, as in Fe₃O₄, which holds Fe²⁺ and Fe³⁺). Each atom has a whole-number state; this is their mean.",
    organic:
      "Organic carbon: this is the average over all carbon atoms. Each carbon has its own number depending on its bonds, so draw the structure to assign them one by one.",
    mixed:
      "Mixed: the atoms of this element are not all alike (thiosulfate has one S at +6 and one at −2), so +2 or +5/2 is an average.",
    nearHydrogen:
      "Hydrogen and the other element have almost the same electronegativity. Textbooks assign hydrogen +1, but this is a convention.",
    tooHigh:
      "The result is above +8, which no element reaches. The formula probably contains a peroxide (O–O) group, so the sum rule overstates the metal. Enter the real structure or treat it as a peroxo compound.",
  },
  bookkeeping:
    "Oxidation numbers are bookkeeping, not real charges: they show where the bonding electrons are assigned, which is what you need to follow a redox reaction.",
  errors: {
    inconsistent:
      "No set of oxidation numbers satisfies the rules for this formula. Check the formula and the charge.",
    ambiguous:
      "The rules cannot decide this one: {elements} have no fixed number. Split it into ions (for example Fe^2+ and SO4^2-) or enter a complex ion with its charge, such as [Fe(CN)6]4-.",
    ambiguousGeneral:
      "The rules cannot decide this one: more than one element has no fixed number. Split it into ions or enter a complex ion with its charge.",
  },
  formulaErrors: FORMULA_ERROR_STRINGS,
  copy: "Copy result",
  copied: "Copied!",
  copyLine: "{symbol}: {state}",
} as const;

type Strings = typeof OXIDATION_NUMBER_CALCULATOR_STRINGS;

const EXAMPLES = [
  "K2Cr2O7",
  "H2SO4",
  "MnO4-",
  "H2O2",
  "NaH",
  "FeSO4",
  "Fe3O4",
  "C6H12O6",
  "NH4NO3",
] as const;

function stateOf(assignment: Assignment): string {
  return stateText(assignment.state);
}

function EnvironmentTable({
  env,
  strings,
  fmt,
}: {
  env: Environment;
  strings: Strings;
  fmt: (value: number) => string;
}) {
  const heading =
    env.multiplicity === 1
      ? t(strings.speciesOnce, { species: env.label })
      : t(strings.perSpecies, {
          species: env.label,
          count: fmt(env.multiplicity),
        });
  const terms = env.assignments
    .map((a) => t(strings.sumTerm, { count: fmt(a.count), state: stateOf(a) }))
    .join(" + ");
  const total = stateText(frac(env.charge));
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{heading}</h3>
      <DataTable
        head={[
          strings.columnElement,
          strings.columnAtoms,
          strings.columnNumber,
          strings.columnStock,
          strings.columnRule,
        ]}
        rows={env.assignments.map((a) => [
          `${getElement(a.symbol)?.name ?? a.symbol} (${a.symbol})`,
          fmt(a.count),
          stateOf(a),
          stockNotation(a.symbol, a.state) ?? strings.noStock,
          strings.rules[a.rule],
        ])}
      />
      <div className={WORKING_BOX}>
        <p>{t(strings.sumLine, { terms, total })}</p>
      </div>
    </div>
  );
}

function Flags({
  environments,
  strings,
}: {
  environments: readonly Environment[];
  strings: Strings;
}) {
  const seen = new Map<FlagKey, string[]>();
  for (const env of environments) {
    for (const a of env.assignments) {
      for (const flag of a.flags) {
        seen.set(flag, [...(seen.get(flag) ?? []), a.symbol]);
      }
    }
  }
  if (seen.size === 0) {
    return null;
  }
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.flagsHeading}</h3>
      {[...seen.keys()].map((flag) => (
        <Notice key={flag}>{strings.flags[flag]}</Notice>
      ))}
    </div>
  );
}

function Results({ text, strings }: { text: string; strings: Strings }) {
  const fmt = useToolNumber();
  if (text.trim() === "") {
    return <Notice>{strings.enter}</Notice>;
  }
  const result = assignOxidation(text);
  if (!result.ok) {
    if (result.reason === "parse") {
      return (
        <ErrorNotice>
          {formulaErrorMessage(strings.formulaErrors as FormulaErrorStrings, {
            code: result.code as keyof FormulaErrorStrings,
            token: result.token,
          })}
        </ErrorNotice>
      );
    }
    if (result.reason === "ambiguous") {
      return (
        <ErrorNotice>
          {result.elements.length > 0
            ? t(strings.errors.ambiguous, {
                elements: result.elements.join(", "),
              })
            : strings.errors.ambiguousGeneral}
        </ErrorNotice>
      );
    }
    return <ErrorNotice>{strings.errors.inconsistent}</ErrorNotice>;
  }
  const copy = result.environments
    .flatMap((env) =>
      env.assignments.map((a) =>
        t(strings.copyLine, { symbol: a.symbol, state: stateOf(a) })
      )
    )
    .join(", ");
  return (
    <>
      {result.environments.map((env) => (
        <EnvironmentTable
          env={env}
          fmt={(value) => fmt(value)}
          key={`${env.label}-${env.multiplicity}`}
          strings={strings}
        />
      ))}
      <Flags environments={result.environments} strings={strings} />
      <p className="text-muted-foreground text-xs">{strings.bookkeeping}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={copy}
      />
    </>
  );
}

export default function OxidationNumberCalculatorWidget() {
  const strings = useToolUi(OXIDATION_NUMBER_CALCULATOR_STRINGS);
  const [text, setText] = useState<string>(EXAMPLES[0]);
  return (
    <>
      <TextField
        hint={strings.formulaHint}
        id="oxn-formula"
        label={strings.formulaLabel}
        mono
        onChange={setText}
        placeholder={strings.formulaPlaceholder}
        value={text}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className={LABEL}>{strings.tryLabel}</span>
        {EXAMPLES.map((example) => (
          <button
            className={BUTTON_SMALL}
            key={example}
            onClick={() => setText(example)}
            type="button"
          >
            {example}
          </button>
        ))}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Results strings={strings} text={text} />
      </section>
    </>
  );
}
